/**
 * Универсальное модальное окно
 * Поддерживает: заголовок, контент, кнопки действий, закрытие по ESC и клику вне окна
 */

import React, { useEffect, useRef } from 'react';

interface ModalProps {
  /** Показывать ли модалку */
  isOpen: boolean;
  /** Заголовок окна */
  title: string;
  /** Закрыть окно */
  onClose: () => void;
  /** Дочерние элементы — содержимое модалки */
  children: React.ReactNode;
  /** Ширина модалки: sm (400px), md (560px), lg (720px), xl (960px) */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export const Modal: React.FC<ModalProps> = ({ isOpen, title, onClose, children, size = 'md' }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Закрытие по клавише ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Блокируем скролл страницы
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Закрытие по клику на оверлей (вне контента)
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm animate-fadeIn pt-16 pb-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={contentRef}
        className={`${sizeClasses[size]} w-full mx-4 bg-white rounded-xl shadow-2xl animate-slideDown`}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Закрыть"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Контент */}
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
};

// ============================================================
// Footer модалки с кнопками действий
// ============================================================

interface ModalFooterProps {
  /** Кнопка отмены */
  onCancel: () => void;
  /** Текст кнопки отмены */
  cancelText?: string;
  /** Кнопка подтверждения */
  onConfirm: () => void;
  /** Текст кнопки подтверждения */
  confirmText?: string;
  /** Загрузка — блокирует кнопки */
  loading?: boolean;
  /** Вариант кнопки подтверждения */
  variant?: 'primary' | 'danger';
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  onCancel,
  cancelText = 'Отмена',
  onConfirm,
  confirmText = 'Сохранить',
  loading = false,
  variant = 'primary',
}) => {
  const confirmClasses =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';

  return (
    <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 transition-colors"
      >
        {cancelText}
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={loading}
        className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 transition-colors ${confirmClasses}`}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Сохранение...
          </span>
        ) : (
          confirmText
        )}
      </button>
    </div>
  );
};

// ============================================================
// CSS анимации для модалки (добавить в глобальные стили)
// ============================================================

export const modalStyles = `
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}

.animate-slideDown {
  animation: slideDown 0.25s ease-out;
}
`;
